import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useUserTrialDetails } from '@revelio/auth';
import {
  TALENT_DISCOVERY_BY_ID_V3,
  type TalentDiscoveryByIdReqV3,
  type TalentDiscoveryByIdRespV3,
} from '@revelio/data-access';

import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  getRoleTaxonomyId,
  getRoleTaxonomyNumber,
} from '../../DataProvider/talentDiscovery/roles';
import { getSkillTaxonomyId } from '../../DataProvider/talentDiscovery/skills';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { useUrqlQueryWithStatus } from '../../DataProvider/utils/data-provider-results-utils';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import { useSplitPane } from '../../SplitPanel/SplitPaneContext';
import type { TableRow } from '../../TableSelectionProvider/TableSelectionProvider';
import { ChartNotSupported } from '../ChartNotSupported';
import {
  FullProfileInfo,
  FullProfileInfoLoading,
} from '../FullProfileInfo/FullProfileInfo';
import {
  transformByIdUserToProfileData,
  transformTableRowToProfileData,
} from '../FullProfileInfo/transformTableRowToProfileData';
import styles from './RowPopoutSelector.module.css';
import tableStyles from './Table.module.css';
import { usePopoverPositioning } from './usePopoverPositioning';

type PopoutChildProps<T extends HTMLElement> = {
  onClick: (e: React.MouseEvent<T>) => void;
  anchorRef: (node: T | null) => void;
  isHighlighted: boolean;
};

type BasePopoutProps<T extends HTMLElement> = {
  children: (props: PopoutChildProps<T>) => ReactNode;
  gridRef?: {
    getScrollLeft: () => number;
    getDOMNode: () => HTMLDivElement | null;
  };
  boundaryRef: React.RefObject<HTMLDivElement>;
  content: ReactNode;
  highlightClassName?: string;
};

const isRectIntersecting = (
  rect: DOMRect,
  bounds: { top: number; right: number; bottom: number; left: number }
): boolean =>
  rect.bottom > bounds.top &&
  rect.top < bounds.bottom &&
  rect.right > bounds.left &&
  rect.left < bounds.right;

function BasePopout<T extends HTMLElement>({
  children,
  gridRef,
  boundaryRef: _boundaryRef,
  content,
  highlightClassName,
}: BasePopoutProps<T>) {
  const anchorRef = useRef<T | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { expandRightPane } = useSplitPane();

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const { placement, chevronTop, maxWidth, rowHeight } = usePopoverPositioning({
    anchorRef,
    popperElement,
    isOpen,
    gridRef,
    boundaryRef: _boundaryRef,
  });

  const handleAnchorClick = (e: React.MouseEvent<T>) => {
    e.stopPropagation();
    // Opening the detail popout: make sure the (possibly collapsed) right pane
    // is expanded so the popout isn't clipped.
    if (!isOpen) {
      expandRightPane();
    }
    setIsOpen((prev) => !prev);
  };

  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        popperElement &&
        !popperElement.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, popperElement]);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !highlightClassName) return;

    anchor.classList.toggle(highlightClassName, isOpen);
    return () => anchor.classList.remove(highlightClassName);
  }, [isOpen, highlightClassName]);

  useEffect(() => {
    if (!isOpen) return;

    const boundaryNode = _boundaryRef.current;
    const handleVisibilityCheck = () => {
      const anchor = anchorRef.current;
      if (!anchor) {
        setIsOpen(false);
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const viewportBounds = {
        top: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
        left: 0,
      };

      if (!isRectIntersecting(anchorRect, viewportBounds)) {
        setIsOpen(false);
        return;
      }

      if (boundaryNode) {
        const boundaryRect = boundaryNode.getBoundingClientRect();
        if (!isRectIntersecting(anchorRect, boundaryRect)) {
          setIsOpen(false);
        }
      }
    };

    handleVisibilityCheck();

    if (boundaryNode) {
      boundaryNode.addEventListener('scroll', handleVisibilityCheck);
    }
    window.addEventListener('scroll', handleVisibilityCheck, true);
    window.addEventListener('resize', handleVisibilityCheck);

    return () => {
      if (boundaryNode) {
        boundaryNode.removeEventListener('scroll', handleVisibilityCheck);
      }
      window.removeEventListener('scroll', handleVisibilityCheck, true);
      window.removeEventListener('resize', handleVisibilityCheck);
    };
  }, [isOpen, _boundaryRef]);

  const chevronHalfHeight =
    rowHeight && rowHeight > 0 ? Math.min(rowHeight / 2, 18) : 18;

  return (
    <>
      {children({
        onClick: handleAnchorClick,
        anchorRef: (node) => {
          anchorRef.current = node;
        },
        isHighlighted: isOpen,
      })}
      {isOpen &&
        createPortal(
          <div
            ref={setPopperElement}
            data-testid="full-profile-info-popout"
            data-side={placement.startsWith('right') ? 'right' : 'left'}
            className={styles.popoutContainer}
            style={{
              position: 'absolute',
              maxWidth: `${maxWidth}px`,
            }}
            onClick={(event: MouseEvent<HTMLDivElement>) =>
              event.stopPropagation()
            }
          >
            {/* Arrow connector pointing to the row */}
            {chevronTop !== null && (
              <div
                className={styles.arrow}
                style={{
                  position: 'absolute',
                  ...(placement.startsWith('right') && {
                    left: '-20px',
                    top: `${chevronTop}px`,
                    transform: 'translateY(-50%)',
                  }),
                  ...(placement.startsWith('left') && {
                    right: '-20px',
                    top: `${chevronTop}px`,
                    transform: 'translateY(-50%)',
                  }),
                }}
              >
                <div
                  className={styles.arrowChevron}
                  style={
                    {
                      '--chevron-half-height': `${chevronHalfHeight}px`,
                    } as CSSProperties
                  }
                />
              </div>
            )}
            <div className={styles.popoutContent}>
              <div className={styles.closeButtonContainer}>
                <button
                  aria-label="Close profile popout"
                  onClick={handleCloseClick}
                  className={styles.closeButton}
                >
                  <IconCrossLarge size={14} />
                </button>
              </div>
              <div className={styles.content}>
                <div className={styles.scrollContent}>{content}</div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export const RowPopout = ({
  children,
  gridRef,
  boundaryRef,
  content,
}: {
  children: (props: PopoutChildProps<HTMLTableRowElement>) => ReactNode;
  gridRef?: {
    getScrollLeft: () => number;
    getDOMNode: () => HTMLDivElement | null;
  };
  boundaryRef: React.RefObject<HTMLDivElement>;
  rowData?: TableRow;
  content: ReactNode;
}) => (
  <BasePopout<HTMLTableRowElement>
    highlightClassName={tableStyles.rowHighlighted}
    boundaryRef={boundaryRef}
    gridRef={gridRef}
    content={content}
  >
    {children}
  </BasePopout>
);

export const CardPopout = ({
  children,
  gridRef,
  boundaryRef,
  content,
}: {
  children: (props: PopoutChildProps<HTMLDivElement>) => ReactNode;
  gridRef?: {
    getScrollLeft: () => number;
    getDOMNode: () => HTMLDivElement | null;
  };
  boundaryRef: React.RefObject<HTMLDivElement>;
  rowData?: TableRow;
  content: ReactNode;
}) => (
  <BasePopout<HTMLDivElement>
    highlightClassName={tableStyles.rowHighlighted}
    boundaryRef={boundaryRef}
    gridRef={gridRef}
    content={content}
  >
    {children}
  </BasePopout>
);

export const PopoutContentSelector = ({
  chartType,
  rowData,
}: {
  chartType: ChartType;
  rowData: TableRow;
}) => {
  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });
  const effectiveFilters = useEffectiveFilters();
  const rolesTax = useMemo(
    () => getRoleTaxonomyId(effectiveFilters),
    [effectiveFilters]
  );
  const skillsTax = useMemo(
    () => getSkillTaxonomyId(effectiveFilters),
    [effectiveFilters]
  );
  // CHANGE 2: numeric role taxonomy id for the new `roles_tax_id` field.
  const rolesTaxId = useMemo(
    () => getRoleTaxonomyNumber(effectiveFilters),
    [effectiveFilters]
  );

  const originalUserId = useMemo(() => {
    const toNumericId = (value: unknown): number | null => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    const directId = toNumericId(rowData?.original_user_id);
    if (directId !== null) {
      return directId;
    }

    const meta = rowData?.__metaData;
    if (meta && typeof meta === 'object') {
      const metaId = toNumericId(
        (meta as Record<string, unknown>).original_user_id
      );
      if (metaId !== null) {
        return metaId;
      }
    }

    return 0;
  }, [rowData]);

  const rowProfileData = useMemo(
    () => transformTableRowToProfileData(rowData),
    [rowData]
  );

  const isFullProfileInfo = chartType === 'full-profile-info';

  const shouldFetchById =
    isFullProfileInfo && !isTrialUser && originalUserId > 0;
  const byIdVariables = useMemo(
    () => ({
      query: {
        filters: {
          roles_tax: rolesTax,
          skills_tax: skillsTax,
          // CHANGE 2: dual-send new int taxonomy ids (backward compat).
          roles_tax_id: rolesTaxId,
          skills_tax_id: skillsTax,
        },
        original_users_ids: [originalUserId],
      },
    }),
    [rolesTax, skillsTax, rolesTaxId, originalUserId]
  );

  const [{ data: byIdData, status: byIdStatus }] = useUrqlQueryWithStatus<
    { talent_discovery_search_by_id_v3: TalentDiscoveryByIdRespV3 },
    { query: TalentDiscoveryByIdReqV3 }
  >({
    query: TALENT_DISCOVERY_BY_ID_V3,
    variables: byIdVariables,
    pause: !shouldFetchById,
  });

  if (isFullProfileInfo) {
    const isByIdLoading =
      shouldFetchById &&
      (byIdStatus === DataProviderQueryStatus.Fetching ||
        byIdStatus === DataProviderQueryStatus.Idle);
    if (isByIdLoading) {
      return <FullProfileInfoLoading />;
    }

    if (shouldFetchById && byIdStatus === DataProviderQueryStatus.Error) {
      return <FullProfileInfo data={rowProfileData} />;
    }

    const byIdUser =
      shouldFetchById && byIdStatus === DataProviderQueryStatus.Success
        ? (byIdData?.talent_discovery_search_by_id_v3?.users?.[0] ?? null)
        : null;
    const byIdProfileData = byIdUser
      ? transformByIdUserToProfileData(byIdUser)
      : null;

    return <FullProfileInfo data={byIdProfileData ?? rowProfileData} />;
  }
  return <ChartNotSupported />;
};
